import type { ColumnMapping, Tape } from './intakeDDModel';

/**
 * Browser storage for DD Intake. The backend keeps loans, not the tape they
 * came from, so the tape (needed to reopen "Review column mapping") and the
 * files uploaded but not yet extracted are kept per job in localStorage.
 * Storage errors (private mode, quota) are ignored: Intake still works, it just
 * cannot reopen the mapping after a reload.
 */

export interface StoredTape {
  tape: Tape;
  mapping: ColumnMapping;
  /** Tape row index → id of the loan created for it. */
  rowLoanIds: Record<string, string>;
}

/** Loan id → names of files uploaded and not yet sent to extraction. */
export type PendingUploads = Record<string, string[]>;

const tapeKey = (jobId: string) => `docmind.intake.dd.tape.${jobId}`;
const pendingKey = (jobId: string) => `docmind.intake.dd.pending.${jobId}`;

function read<T>(key: string): T | null {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function write(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable */
  }
}

export function loadStoredTape(jobId: string): StoredTape | null {
  const stored = read<StoredTape>(tapeKey(jobId));
  return stored && stored.tape && stored.mapping ? stored : null;
}

export function saveStoredTape(jobId: string, stored: StoredTape): void {
  write(tapeKey(jobId), stored);
}

export function loadPendingUploads(jobId: string): PendingUploads {
  return read<PendingUploads>(pendingKey(jobId)) ?? {};
}

export function savePendingUploads(jobId: string, pending: PendingUploads): void {
  write(pendingKey(jobId), pending);
}
